import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorFallback } from '~/components/ErrorFallback/ErrorFallback';
import { DOMAIN, REASON, reportError } from '~/services/telemetry';
import { isStaleChunkError } from '~/utils/staleChunk';

interface BoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  /** Changing this clears the error, so the next modal opens normally. */
  resetKey: string;
}

class Boundary extends Component<BoundaryProps, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunkFailure = isStaleChunkError(error);

    reportError(isChunkFailure ? DOMAIN.chunk : DOMAIN.ui, error, {
      reason: isChunkFailure
        ? REASON.chunk.loadFailed
        : REASON.ui.modalRenderFailed,
      // A chunk lost to a deploy is routine, not a fault; reported as an
      // error it would move the error rate every release.
      level: isChunkFailure ? 'warning' : 'error',
      extras: { errorInfo },
    });
  }

  componentDidUpdate(previous: BoundaryProps) {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * Keeps a modal that fails to render from taking the whole app down.
 *
 * `ModalList` sits outside `RouteErrorBoundary` — it is a sibling of the router
 * in `App`, because a modal has to survive navigation. So an error thrown while
 * a modal renders skipped every boundary until the one at the root, which
 * renders `null`: a modal whose chunk never arrived blanked the entire page,
 * with not even the error screen's reload button left to escape by.
 *
 * The fallback stays on top as an overlay rather than disappearing, which
 * matters for the age gate: it is the one modal whose absence would let a
 * visitor straight into the catalogue. It is dismissed by reloading, the same
 * recovery `lazyRoute` performs automatically for the first failure.
 */
export const ModalErrorBoundary = ({
  children,
  resetKey,
}: {
  children: ReactNode;
  resetKey: string;
}) => (
  <Boundary
    resetKey={resetKey}
    fallback={
      // `z-[1000]` is the modal layer (`$zindex_modal`), not an arbitrary
      // number: this stands in for a modal, and the header sits at 100 — at the
      // default stacking it would cover the scrim. The scrim itself is a
      // literal rather than a token, because a see-through overlay would defeat
      // the age gate this fallback has to keep standing.
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 px-6">
        <div className="w-full max-w-[400px] rounded-5 bg-bg-content py-10">
          <ErrorFallback />
        </div>
      </div>
    }
  >
    {children}
  </Boundary>
);
